"use client"
import React, { useState, useEffect } from 'react'
import { BicoinLogo } from '../../components/BicoinLogo'
import { useNavigate } from 'react-router-dom'
import { Search, Menu, TrendingUp, LogOut, Repeat, Wallet, Target, Grid3X3, GraduationCap, Megaphone, X, ShieldCheck, Users, Radio, History, HeadphonesIcon, Bell, Coins, ArrowLeftRight, Shuffle, Gift, UserPlus, MoreHorizontal, ArrowLeft, Star } from 'lucide-react'
import Layout from '../../components/Layout/Layout'
import { useAuth } from '../../context/AuthContext'
import { getDetailedMarketData } from '../../services/tradeService'
import { getAnnouncementsService, getBannersService } from '../../services/userService'
import { getBalance } from '../../services/walletService'
import '../../styles/dashboard.css'
import '../../styles/landing.css'

// ── Services categories (image 4 layout) ─────────────────────────────────────
const SERVICE_CATEGORIES = [
    {
        title: 'Recommend',
        items: [
            { icon: Gift,          label: 'Rewards',      path: '/dashboard/campaign' },
            { icon: UserPlus,      label: 'Referral',     path: '/dashboard/referral' },
            { icon: Radio,         label: 'Signals',      path: '/dashboard/signals' },
            { icon: Users,         label: 'P2P',          path: '/p2p' },
        ],
    },
    {
        title: 'Common Function',
        items: [
            { icon: Wallet,        label: 'Deposit',      path: '/dashboard/deposit' },
            { icon: ArrowLeftRight,label: 'Transfer',     path: '/dashboard/transfer' },
            { icon: Shuffle,       label: 'Convert',      path: '/dashboard/convert' },
            { icon: Wallet,        label: 'Withdraw',     path: '/dashboard/withdraw' },
        ],
    },
    {
        title: 'Earn',
        items: [
            { icon: Coins,         label: 'Earn',         path: '/dashboard/earn' },
            { icon: Grid3X3,       label: 'Futures',      path: '/dashboard/futures' },
            { icon: Repeat,        label: 'Trade',        path: '/dashboard/trade' },
        ],
    },
    {
        title: 'Information',
        items: [
            { icon: Megaphone,     label: 'Announcements',path: '/dashboard/home' },
            { icon: GraduationCap, label: 'Academy',      path: '/dashboard/academy' },
            { icon: HeadphonesIcon,label: 'Support',      path: '/dashboard/support' },
        ],
    },
    {
        title: 'Others',
        items: [
            { icon: History,       label: 'History',      path: '/dashboard/history' },
            { icon: Target,        label: 'Assets',       path: '/dashboard/assets' },
            { icon: ShieldCheck,   label: 'KYC',          path: '/dashboard/kyc' },
        ],
    },
]

// ── Horizontal shortcut row — first 5 items shown, rest in Services ───────────
const SHORTCUT_ITEMS = [
    { icon: Gift,          label: 'Rewards',      path: '/dashboard/campaign' },
    { icon: UserPlus,      label: 'Referral',     path: '/dashboard/referral' },
    { icon: Coins,         label: 'Earn',         path: '/dashboard/earn' },
    { icon: Radio,         label: 'Signals',      path: '/dashboard/signals' },
    { icon: Users,         label: 'P2P',          path: '/p2p' },
]

const homeCoinLogos: Record<string, string> = {
    BTC: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png',
    ETH: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png',
    XRP: 'https://s2.coinmarketcap.com/static/img/coins/64x64/52.png',
    SOL: 'https://assets.coingecko.com/coins/images/4128/small/solana.png',
    AVAX: 'https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png',
    DOGE: 'https://assets.coingecko.com/coins/images/5/small/dogecoin.png',
    BNB: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png',
    ADA: 'https://assets.coingecko.com/coins/images/975/small/cardano.png',
}

const homeSymbols = ['BTC/USDT', 'ETH/USDT', 'XRP/USDT', 'SOL/USDT', 'AVAX/USDT', 'DOGE/USDT', 'BNB/USDT', 'ADA/USDT']

export default function Home() {
    const navigate = useNavigate()
    const { isLoggedIn, user, logout } = useAuth()

    const [mobileCryptoData, setMobileCryptoData] = useState([
        { symbol: 'BTC', name: '/USDT', logo: homeCoinLogos.BTC, price: '—', change: '—', positive: true, volume: '—' },
        { symbol: 'ETH', name: '/USDT', logo: homeCoinLogos.ETH, price: '—', change: '—', positive: true, volume: '—' },
        { symbol: 'XRP', name: '/USDT', logo: homeCoinLogos.XRP, price: '—', change: '—', positive: true, volume: '—' },
        { symbol: 'SOL', name: '/USDT', logo: homeCoinLogos.SOL, price: '—', change: '—', positive: true, volume: '—' },
        { symbol: 'AVAX', name: '/USDT', logo: homeCoinLogos.AVAX, price: '—', change: '—', positive: true, volume: '—' },
        { symbol: 'DOGE', name: '/USDT', logo: homeCoinLogos.DOGE, price: '—', change: '—', positive: true, volume: '—' },
        { symbol: 'BNB', name: '/USDT', logo: homeCoinLogos.BNB, price: '—', change: '—', positive: true, volume: '—' },
        { symbol: 'ADA', name: '/USDT', logo: homeCoinLogos.ADA, price: '—', change: '—', positive: true, volume: '—' },
    ])

    useEffect(() => {
        let ws: WebSocket | null = null;
        let retryTimeout: any;
        let fallbackPollInterval: any;
        let isWsConnected = false;

        const formatVol = (n: number) => {
            if (!n) return '—';
            if (n > 1e9) return `$${(n / 1e9).toFixed(2)}B`;
            if (n > 1e6) return `$${(n / 1e6).toFixed(2)}M`;
            return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
        };

        const applyMarketSnapshot = (snapshot: Record<string, any>) => {
            setMobileCryptoData(prev => {
                let hasChanges = false;
                const next = prev.map(item => {
                    const key = `${item.symbol}/USDT`;
                    const data = snapshot[key];
                    if (!data || typeof data.price !== 'number' || data.price <= 0) return item;

                    const p = data.price;
                    const changePct = typeof data.change24h === 'number' ? data.change24h : 0;
                    const vol = typeof data.volume24h === 'number' ? data.volume24h : 0;

                    const updated = {
                        ...item,
                        price: `$${p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        change: `${changePct >= 0 ? '+' : ''}${changePct.toFixed(2)}%`,
                        positive: changePct >= 0,
                        volume: formatVol(vol),
                    };

                    if (
                        updated.price !== item.price ||
                        updated.change !== item.change ||
                        updated.volume !== item.volume ||
                        updated.positive !== item.positive
                    ) {
                        hasChanges = true;
                    }

                    return updated;
                });
                return hasChanges ? next : prev;
            });
        };

        const fetchFallbackPrices = async () => {
            try {
                // Fetch directly from Binance — same source as WebSocket, includes 24h change
                const syms = homeSymbols.map(s => s.replace('/', ''));
                const res = await fetch(`https://api.binance.com/api/v3/ticker/24hr?symbols=${JSON.stringify(syms)}`);
                if (!res.ok) throw new Error('Binance REST failed');
                const tickers: any[] = await res.json();
                setMobileCryptoData(prev => {
                    const next = prev.map(item => {
                        const ticker = tickers.find(t => t.symbol === item.symbol + 'USDT');
                        if (!ticker) return item;
                        const p = parseFloat(ticker.lastPrice);
                        const changePct = parseFloat(ticker.priceChangePercent);
                        const vol = parseFloat(ticker.quoteVolume);
                        if (!p || isNaN(p)) return item;
                        return {
                            ...item,
                            price: `$${p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                            change: `${changePct >= 0 ? '+' : ''}${changePct.toFixed(2)}%`,
                            positive: changePct >= 0,
                            volume: formatVol(vol),
                        };
                    });
                    return next;
                });
            } catch {
                // Binance failed — try backend as last resort
                try {
                    const snapshot = await getDetailedMarketData(homeSymbols);
                    applyMarketSnapshot(snapshot);
                } catch { /* keep current values */ }
            }
        };

        const connectWS = () => {
            try {
                ws = new WebSocket('wss://stream.binance.com:9443/ws/!ticker@arr');
                ws.onopen = () => { isWsConnected = true; };
                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        if (Array.isArray(data)) {
                            setMobileCryptoData(prev => {
                                let hasChanges = false;
                                const next = prev.map(item => {
                                    const wsSym = item.symbol + 'USDT';
                                    const ticker = data.find((t: any) => t.s === wsSym);
                                    if (ticker) {
                                        hasChanges = true;
                                        const p = parseFloat(ticker.c);
                                        const changePct = parseFloat(ticker.P);
                                        const vol = parseFloat(ticker.q);

                                        return {
                                            ...item,
                                            price: `$${p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                                            change: `${changePct >= 0 ? '+' : ''}${changePct.toFixed(2)}%`,
                                            positive: changePct >= 0,
                                            volume: formatVol(vol),
                                        };
                                    }
                                    return item;
                                });
                                return hasChanges ? next : prev;
                            });
                        }
                    } catch (e) { /* ignore */ }
                };
                ws.onerror = () => { isWsConnected = false; if (ws) ws.close(); };
                ws.onclose = () => { isWsConnected = false; retryTimeout = setTimeout(connectWS, 5000); };
            } catch (err) {
                isWsConnected = false;
                retryTimeout = setTimeout(connectWS, 5000);
            }
        };

        fetchFallbackPrices();
        fallbackPollInterval = setInterval(() => {
            if (!isWsConnected) fetchFallbackPrices();
        }, 12000);

        connectWS();
        return () => {
            clearTimeout(retryTimeout);
            clearInterval(fallbackPollInterval);
            if (ws) {
                ws.onclose = null;
                ws.onerror = null;
                ws.close();
            }
        };
    }, []);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [showSearch, setShowSearch] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [announcements, setAnnouncements] = useState<{ title: string; body: string }[]>([])
    const [userBalance, setUserBalance] = useState(0)
    const [futuresBalance, setFuturesBalance] = useState(0)
    const [balanceLoading, setBalanceLoading] = useState(true)
    const [announcementsLoading, setAnnouncementsLoading] = useState(true)
    const [banners, setBanners] = useState<{ _id: string; imageUrl: string; linkUrl?: string; title?: string }[]>([])
    const [bannerIndex, setBannerIndex] = useState(0)
    const [showServices, setShowServices] = useState(false)
    const [marketTab, setMarketTab] = useState<'Favorites'|'Hot'|'Gainers'|'New'>('Favorites')
    const [marketSubTab, setMarketSubTab] = useState<'All'|'Spot'|'Futures'>('All')
    const [localFiat, setLocalFiat] = useState<string>(() => localStorage.getItem('p2p_selected_fiat') || 'USD')
    const [fiatRate, setFiatRate] = useState<number>(1)
    const [showFiatDropdown, setShowFiatDropdown] = useState(false)

    const FIATS = ['USD','KES','UGX','TZS','PKR','NGN','ZAR','INR','GHS','RWF']
    const COUNTRY_FIAT: Record<string,string> = {
        Kenya:'KES', Uganda:'UGX', Tanzania:'TZS', Rwanda:'RWF',
        Nigeria:'NGN', Ghana:'GHS', 'South Africa':'ZAR',
        Pakistan:'PKR', India:'INR', Philippines:'PHP',
    }

    // Auto-detect fiat from user country if not saved
    useEffect(() => {
        if (localStorage.getItem('p2p_selected_fiat')) return
        const token = (() => { try { return JSON.parse(localStorage.getItem('userInfo') || '{}').token } catch { return null } })()
        if (!token) return
        fetch(`${import.meta.env.VITE_API_BASE_URL}/api/p2p/settings/fees`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json())
            .then(d => {
                const country = d?.data?.userCountry
                if (country && COUNTRY_FIAT[country]) {
                    setLocalFiat(COUNTRY_FIAT[country])
                    localStorage.setItem('p2p_selected_fiat', COUNTRY_FIAT[country])
                }
            }).catch(() => {})
    }, [])

    // Fetch fiat rate whenever localFiat changes
    useEffect(() => {
        if (localFiat === 'USD') { setFiatRate(1); return }
        fetch(`${import.meta.env.VITE_API_BASE_URL}/api/p2p/market-price?crypto=USDT&fiat=${localFiat}`)
            .then(r => r.json())
            .then(d => { if (d?.data?.price > 0) setFiatRate(d.data.price) })
            .catch(() => {})
    }, [localFiat])

    const searchItems = [
        { label: 'Home', path: '/dashboard/home' },
        { label: 'Market', path: '/dashboard/market' },
        { label: 'Trade', path: '/dashboard/trade' },
        { label: 'P2P', path: '/p2p' },
        { label: 'Futures', path: '/dashboard/futures' },
        { label: 'Copy Trading', path: '/dashboard/signals' },
        { label: 'Deposit', path: '/dashboard/deposit' },
        { label: 'Withdraw', path: '/dashboard/withdraw' },
        { label: 'Transfer', path: '/dashboard/transfer' },
        { label: 'Assets', path: '/dashboard/assets' },
        { label: 'Earn', path: '/dashboard/earn' },
        { label: 'History', path: '/dashboard/history' },
        { label: 'Campaign', path: '/dashboard/campaign' },
        { label: 'Academy', path: '/dashboard/academy' },
        { label: 'Support', path: '/dashboard/support' },
        { label: 'Notifications', path: '/dashboard/notifications' },
        { label: 'Profile', path: '/dashboard/profile' },
        { label: 'KYC Verification', path: '/dashboard/kyc' },
        { label: 'Settings', path: '/dashboard/settings' },
    ]
    const filteredSearch = searchQuery.trim().length > 0
        ? searchItems.filter(i => i.label.toLowerCase().includes(searchQuery.toLowerCase()))
        : searchItems

    useEffect(() => {
        getAnnouncementsService().then(data => { if (data.length > 0) setAnnouncements(data) }).catch(() => {}).finally(() => setAnnouncementsLoading(false))
    }, [])

    useEffect(() => {
        getBannersService().then(data => { if (data.length > 0) setBanners(data) }).catch(() => {})
    }, [])

    useEffect(() => {
        if (banners.length <= 1) return
        const t = setInterval(() => setBannerIndex(i => (i + 1) % banners.length), 4000)
        return () => clearInterval(t)
    }, [banners.length])

    useEffect(() => {
        getBalance().then(data => { setUserBalance(data.balance || 0); setFuturesBalance((data as any).futuresBalance || 0) }).catch(() => {}).finally(() => setBalanceLoading(false))
    }, [])

    return (
        <Layout activePage="home" hideFooter={false} hideFooterMobile={true}>
            <div className="mobile-home-view">
                <div className="mobile-header-bar">
                    <div className="mobile-header-left">
                        <BicoinLogo height={32} className="mobile-header-logo" />
                    </div>
                    <div className="mobile-header-right">
                        <div className="mobile-header-icon" onClick={() => { setShowSearch(true); setSearchQuery(''); }}>
                            <Search size={22} />
                        </div>
                        <div className="mobile-header-icon" onClick={() => navigate('/dashboard/notifications')}>
                            <Bell size={22} />
                        </div>
                        <div className="mobile-header-icon" onClick={() => setMobileMenuOpen(true)}>
                            <Menu size={22} />
                        </div>
                    </div>
                </div>

                <div style={{ padding: '16px 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <div style={{ fontSize: 13, color: 'var(--ui-text-muted)', marginBottom: 4 }}>Total Value</div>
                        {balanceLoading
                            ? <div className="home-skel home-skel-balance-sm" />
                            : <>
                                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--ui-text-primary)', lineHeight: 1.1 }}>
                                    {(userBalance + futuresBalance).toFixed(2)} <span style={{ fontSize: 16, fontWeight: 400, color: 'var(--ui-text-secondary)' }}>USDT</span>
                                </div>
                                <div style={{ fontSize: 13, color: 'var(--ui-text-muted)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    ≈ {((userBalance + futuresBalance) * fiatRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    {/* Fiat selector */}
                                    <div style={{ position: 'relative', display: 'inline-flex' }}>
                                        <button onClick={() => setShowFiatDropdown(v => !v)}
                                            style={{ background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 6, padding: '2px 8px 2px 6px', color: 'var(--ui-text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                                            {localFiat}
                                            <span style={{ fontSize: 9 }}>▼</span>
                                        </button>
                                        {showFiatDropdown && (
                                            <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 8, zIndex: 100, overflow: 'hidden', minWidth: 80, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                                                {FIATS.map(f => (
                                                    <div key={f} onClick={() => { setLocalFiat(f); localStorage.setItem('p2p_selected_fiat', f); setShowFiatDropdown(false) }}
                                                        style={{ padding: '8px 14px', fontSize: 13, fontWeight: f === localFiat ? 700 : 400, color: f === localFiat ? '#1CD4A7' : '#e4e4e7', cursor: 'pointer', background: f === localFiat ? 'rgba(28,212,167,0.08)' : 'none' }}>
                                                        {f}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        }
                    </div>
                    <button onClick={() => navigate('/dashboard/deposit')}
                        style={{ background: '#3B82F6', border: 'none', borderRadius: 8, padding: '10px 20px', color: 'var(--ui-text-primary)', fontWeight: 700, fontSize: 14, cursor: 'pointer', flexShrink: 0 }}>
                        Deposit
                    </button>
                </div>

                {/* Mobile Search Overlay */}
                {showSearch && (
                    <div onClick={() => setShowSearch(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
                        <div onClick={e => e.stopPropagation()} style={{ background: 'var(--ui-bg-card)', padding: '16px', borderBottom: '1px solid var(--ui-border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--ui-bg-elevated)', borderRadius: 10, padding: '10px 14px' }}>
                                <Search size={18} color="#71717A" />
                                <input
                                    autoFocus
                                    type="text"
                                    placeholder="Search pages..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--ui-text-primary)', fontSize: 15 }}
                                />
                                <button onClick={() => setShowSearch(false)} style={{ background: 'none', border: '1px solid var(--ui-border)', color: 'var(--ui-text-muted)', borderRadius: 4, padding: '2px 8px', fontSize: 12, cursor: 'pointer' }}>✕</button>
                            </div>
                        </div>
                        <div onClick={e => e.stopPropagation()} style={{ flex: 1, overflowY: 'auto', background: 'var(--ui-bg-card)', padding: '8px 0' }}>
                            {filteredSearch.map(item => (
                                <div key={item.path} onClick={() => { navigate(item.path); setShowSearch(false); }}
                                    style={{ padding: '14px 20px', color: 'var(--ui-text-primary)', fontSize: 15, borderBottom: '1px solid var(--ui-border)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <Search size={15} color="#71717A" />
                                    {item.label}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="home-news-ticker">
                    <Megaphone size={16} />
                    <div className="news-content">
                        {announcements.length > 0
                            ? announcements.map((a, i) => <span key={i}>{a.title}</span>)
                            : <>
                                <span>Welcome to Bicoin - The most secure crypto exchange.</span>
                                <span>Check out our new referral program and earn up to 40% rebate!</span>
                            </>
                        }
                    </div>
                </div>

                {/* Mobile Menu Overlay */}
                {/* Mobile Side Drawer */}
                {mobileMenuOpen && (
                    <div onClick={() => setMobileMenuOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1200 }}>
                        <div onClick={e => e.stopPropagation()} style={{ position:'absolute', top:0, right:0, width:'78%', maxWidth:290, height:'100%', background: 'var(--ui-bg-primary)', display:'flex', flexDirection:'column', overflow:'hidden' }}>

                            {/* Header */}
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', borderBottom:'1px solid #1c1c2c', flexShrink:0 }}>
                                <BicoinLogo height={28} />
                                <button onClick={() => setMobileMenuOpen(false)} style={{ background:'none', border:'none', color: 'var(--ui-text-muted)', cursor:'pointer', padding:4, display:'flex' }}><X size={22} /></button>
                            </div>

                            {/* User card */}
                            {isLoggedIn ? (
                                <div style={{ padding:'8px 12px 6px' }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:10, background: 'var(--ui-bg-card)', borderRadius:10, padding:'8px 10px' }}>
                                        <div style={{ width:34, height:34, borderRadius:'50%', background:'linear-gradient(135deg,#06AE7A,#3B82F6)', color: 'var(--ui-text-primary)', fontWeight:700, fontSize:15, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                        </div>
                                        <div style={{ flex:1, minWidth:0 }}>
                                            <div style={{ color: 'var(--ui-text-primary)', fontWeight:600, fontSize:13, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{user?.name || 'User'}</div>
                                            <div style={{ color: 'var(--ui-text-muted)', fontSize:11, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{user?.email || ''}</div>
                                        </div>
                                        <div onClick={() => { navigate('/dashboard/kyc'); setMobileMenuOpen(false); }} style={{ cursor:'pointer', padding:'3px 7px', borderRadius:6, fontSize:11, fontWeight:600, background:(user as any)?.kycStatus==='verified'?'rgba(16,185,129,0.12)':'rgba(246,70,93,0.12)', color:(user as any)?.kycStatus==='verified'?'#10b981':'#F6465D', display:'flex', alignItems:'center', gap:3, flexShrink:0 }}>
                                            <ShieldCheck size={12} />{(user as any)?.kycStatus==='verified'?'Verified':'Unverified'}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ padding:'8px 12px' }}>
                                    <div style={{ display:'flex', gap:8 }}>
                                        <button onClick={() => navigate('/signin')} style={{ flex:1, padding:'8px', borderRadius:8, background:'#06AE7A', color:'#000', fontWeight:600, fontSize:13, border:'none', cursor:'pointer' }}>Sign In</button>
                                        <button onClick={() => navigate('/signup')} style={{ flex:1, padding:'8px', borderRadius:8, background: 'var(--ui-bg-elevated)', color: 'var(--ui-text-primary)', fontWeight:600, fontSize:13, border: '1px solid var(--ui-border)', cursor:'pointer' }}>Sign Up</button>
                                    </div>
                                </div>
                            )}

                            <div style={{ height:1, background: 'var(--ui-bg-elevated)', margin:'2px 0' }} />

                            {/* Nav */}
                            <div style={{ flex:1, overflowY:'auto', overflowX:'hidden', paddingBottom:8 }}>
                                <div style={{ fontSize:10, fontWeight:700, color: 'var(--ui-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', padding:'6px 14px 2px' }}>Navigation</div>
                                {([
                                    ['/dashboard/market',   TrendingUp,     'Market'],
                                    ['/dashboard/trade',    Repeat,         'Trade'],
                                    ['/p2p',                Users,          'P2P'],
                                    ['/dashboard/futures',  Grid3X3,        'Futures'],
                                    ['/dashboard/signals',  Radio,          'Copy Trading'],
                                    ['/dashboard/deposit',  Wallet,         'Deposit'],
                                    ['/dashboard/withdraw', Wallet,         'Withdraw'],
                                    ['/dashboard/transfer', ArrowLeftRight, 'Transfer'],
                                    ['/dashboard/convert',  Shuffle,        'Convert'],
                                    ['/dashboard/assets',   Target,         'Assets'],
                                    ['/dashboard/earn',     Coins,          'Earn'],
                                    ['/dashboard/history',  History,        'History'],
                                    ['/dashboard/support',  HeadphonesIcon, 'Support'],
                                    ['/dashboard/notifications', Bell,      'Notifications'],
                                ] as [string, any, string][]).map(([path, Icon, label]) => (
                                    <div key={path} onClick={() => { navigate(path); setMobileMenuOpen(false); }} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 14px', color: 'var(--ui-text-primary)', fontSize:13, fontWeight:500, cursor:'pointer' }}>
                                        <Icon size={18} style={{ color: 'var(--ui-text-muted)', flexShrink:0 }} /><span>{label}</span>
                                    </div>
                                ))}

                                <div style={{ height:1, background: 'var(--ui-bg-elevated)', margin:'4px 0' }} />
                                <div style={{ fontSize:10, fontWeight:700, color: 'var(--ui-text-secondary)', textTransform:'uppercase', letterSpacing:'0.06em', padding:'6px 14px 2px' }}>Account</div>
                                {([
                                    ['/dashboard/profile',  Target,      'Profile'],
                                    ['/dashboard/kyc',      ShieldCheck, 'KYC Verification'],
                                    ['/dashboard/settings', Grid3X3,     'Settings'],
                                ] as [string, any, string][]).map(([path, Icon, label]) => (
                                    <div key={path} onClick={() => { navigate(path); setMobileMenuOpen(false); }} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 14px', color: 'var(--ui-text-primary)', fontSize:13, fontWeight:500, cursor:'pointer' }}>
                                        <Icon size={18} style={{ color: 'var(--ui-text-muted)', flexShrink:0 }} /><span>{label}</span>
                                    </div>
                                ))}

                            </div>
                            {/* Sticky logout */}
                            {isLoggedIn && (
                                <div style={{ padding:'8px 12px', borderTop:'1px solid #1c1c2c', flexShrink:0, background: 'var(--ui-bg-primary)' }}>
                                    <button onClick={() => { logout(); setMobileMenuOpen(false); navigate('/signin'); }} style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 14px', background:'rgba(246,70,93,0.08)', border:'1px solid rgba(246,70,93,0.2)', borderRadius:10, color:'#F6465D', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                                        <LogOut size={18} /><span>Log Out</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {banners.length > 0 && (
                <div className="mobile-promo-banner" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
                    <>
                        {banners[bannerIndex].linkUrl ? (
                            <a href={banners[bannerIndex].linkUrl} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                                <img src={banners[bannerIndex].imageUrl} alt={banners[bannerIndex].title || 'Banner'} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            </a>
                        ) : (
                            <img src={banners[bannerIndex].imageUrl} alt={banners[bannerIndex].title || 'Banner'} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        )}
                        {banners.length > 1 && (
                            <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 5 }}>
                                {banners.map((_, i) => (
                                    <div key={i} onClick={() => setBannerIndex(i)} style={{ width: i === bannerIndex ? 18 : 6, height: 6, borderRadius: 3, background: i === bannerIndex ? '#1CD4A7' : 'rgba(255,255,255,0.4)', cursor: 'pointer', transition: 'all 0.3s' }} />
                                ))}
                            </div>
                        )}
                    </>
                </div>
                )}

                {/* ── Horizontal shortcut row ── */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '4px 16px 16px', gap: 0, overflowX: 'auto', scrollbarWidth: 'none' }}>
                    {SHORTCUT_ITEMS.map((item, i) => (
                        <div key={i} onClick={() => navigate(item.path)}
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minWidth: 64, cursor: 'pointer', flexShrink: 0 }}>
                            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--ui-bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ui-text-primary)' }}>
                                <item.icon size={22} />
                            </div>
                            <span style={{ fontSize: 11, color: 'var(--ui-text-secondary)', fontWeight: 500, textAlign: 'center', lineHeight: 1.2 }}>{item.label}</span>
                        </div>
                    ))}
                    {/* More button */}
                    <div onClick={() => setShowServices(true)}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minWidth: 64, cursor: 'pointer', flexShrink: 0 }}>
                        <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--ui-bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ui-text-primary)' }}>
                            <MoreHorizontal size={22} />
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--ui-text-secondary)', fontWeight: 500, textAlign: 'center', lineHeight: 1.2 }}>More</span>
                    </div>
                </div>

                {/* ── Services overlay — dark theme ── */}
                {showServices && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'var(--ui-bg-primary)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                        {/* Header */}
                        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 16px', borderBottom: '1px solid #1c1c2c', background: 'var(--ui-bg-primary)', position: 'sticky', top: 0, zIndex: 10 }}>
                            <button onClick={() => setShowServices(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ui-text-secondary)', display: 'flex', marginRight: 16, padding: 4 }}>
                                <ArrowLeft size={22} />
                            </button>
                            <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--ui-text-primary)', flex: 1, textAlign: 'center' }}>Services</span>
                            <div style={{ width: 30 }} />
                        </div>

                        {/* Category sections */}
                        <div style={{ padding: '16px 16px 40px', background: 'var(--ui-bg-primary)' }}>
                            {SERVICE_CATEGORIES.map((cat, ci) => (
                                <div key={ci} style={{ marginBottom: 28 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ui-text-secondary)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '.5px' }}>{cat.title}</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px 8px' }}>
                                        {cat.items.map((item, ii) => (
                                            <div key={ii} onClick={() => { navigate(item.path); setShowServices(false); }}
                                                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                                <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ui-text-primary)' }}>
                                                    <item.icon size={22} />
                                                </div>
                                                <span style={{ fontSize: 11, color: 'var(--ui-text-secondary)', fontWeight: 500, textAlign: 'center', lineHeight: 1.3 }}>{item.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                    {ci < SERVICE_CATEGORIES.length - 1 && (
                                        <div style={{ height: 1, background: 'var(--ui-bg-elevated)', marginTop: 20 }} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Market section with tabs (Biconomy style) ── */}
                <div style={{ paddingBottom: 80 }}>
                    {/* Main tabs */}
                    <div style={{ display: 'flex', padding: '0 16px', gap: 20, borderBottom: '1px solid var(--ui-border)' }}>
                        {(['Favorites', 'Hot', 'Gainers', 'New'] as const).map(t => (
                            <button key={t} onClick={() => setMarketTab(t)}
                                style={{ background: 'none', border: 'none', padding: '10px 0', cursor: 'pointer', fontSize: 14, fontWeight: 600,
                                    color: marketTab === t ? 'var(--ui-text-primary)' : 'var(--ui-text-muted)',
                                    borderBottom: marketTab === t ? '2px solid #1CD4A7' : '2px solid transparent',
                                    transition: '.15s' }}>
                                {t}
                            </button>
                        ))}
                    </div>
                    {/* Sub-tabs */}
                    <div style={{ display: 'flex', padding: '8px 16px', gap: 8 }}>
                        {(['All', 'Spot', 'Futures'] as const).map(t => (
                            <button key={t} onClick={() => {
                                setMarketSubTab(t)
                                if (t === 'Spot') navigate('/dashboard/trade')
                                if (t === 'Futures') navigate('/dashboard/futures')
                            }}
                                style={{ background: marketSubTab === t ? 'var(--ui-bg-elevated)' : 'none', border: marketSubTab === t ? '1px solid var(--ui-border)' : '1px solid transparent', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: marketSubTab === t ? 'var(--ui-text-primary)' : 'var(--ui-text-muted)', transition: '.15s' }}>
                                {t}
                            </button>
                        ))}
                    </div>
                    {/* 2-column grid — filtered by tab */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', background: 'var(--ui-bg-elevated)' }}>
                        {mobileCryptoData[0]?.price === '—'
                            ? [0,1,2,3,4,5].map(i => (
                                <div key={i} style={{ background: 'var(--ui-bg-primary)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    <div className="home-skel" style={{ width: 60, height: 13, borderRadius: 4 }} />
                                    <div className="home-skel" style={{ width: 80, height: 15, borderRadius: 4 }} />
                                    <div className="home-skel" style={{ width: 50, height: 11, borderRadius: 4, opacity: .6 }} />
                                </div>
                              ))
                            : (() => {
                                let list = [...mobileCryptoData]
                                if (marketTab === 'Hot')     list = [...list].sort((a, b) => parseFloat(b.volume.replace(/[$BM]/g,'')) - parseFloat(a.volume.replace(/[$BM]/g,'')))
                                if (marketTab === 'Gainers') list = [...list].sort((a, b) => parseFloat(b.change) - parseFloat(a.change))
                                if (marketTab === 'New')     list = list.slice(-4).reverse()
                                return list.map((coin, index) => (
                                    <div key={index} onClick={() => navigate(`/dashboard/trade?pair=${coin.symbol}/USDT`)}
                                        style={{ background: 'var(--ui-bg-primary)', padding: '14px 16px', cursor: 'pointer', position: 'relative' }}>
                                        <Star size={13} fill="#3B82F6" color="#3B82F6" style={{ position: 'absolute', top: 10, right: 12 }} />
                                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ui-text-primary)', marginBottom: 2 }}>
                                            {coin.symbol}<span style={{ fontSize: 11, fontWeight: 400, color: 'var(--ui-text-muted)' }}>/USDT</span>
                                        </div>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ui-text-primary)', marginBottom: 2, fontVariantNumeric: 'tabular-nums' }}>{coin.price}</div>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: coin.positive ? '#1CD4A7' : '#F6465D' }}>{coin.change}</div>
                                    </div>
                                ))
                              })()
                        }
                    </div>
                    {/* View more */}
                    <div onClick={() => navigate('/dashboard/market')}
                        style={{ textAlign: 'center', padding: '14px', color: '#1CD4A7', fontSize: 13, fontWeight: 600, cursor: 'pointer', borderTop: '1px solid #1c1c2c' }}>
                        View all markets →
                    </div>
                </div>
            </div>

            <div className="desktop-only landing-container">
                <div className="desktop-home-overview">
                    <div className="overview-left">
                        <div className="overview-label">Total Value (USDT)</div>
                        {balanceLoading
                            ? <div className="home-skel home-skel-balance" />
                            : <div className="overview-amount">{(userBalance + futuresBalance).toFixed(2)} ≈ ${(userBalance + futuresBalance).toFixed(2)}</div>
                        }
                        <div className="overview-btns">
                            <button className="overview-btn deposit" onClick={() => navigate('/dashboard/deposit')}>Deposit</button>
                            <button className="overview-btn add-funds" onClick={() => navigate('/dashboard/deposit')}>Add Funds</button>
                        </div>
                    </div>
                    <div className="overview-right">
                        <div className="news-section">
                            <div className="news-header">
                                <Megaphone size={18} />
                                <span>Announcements</span>
                            </div>
                            <div className="news-list">
                                {announcementsLoading
                                    ? [0,1,2].map(i => <div key={i} className="home-skel home-skel-news" />)
                                    : announcements.length > 0
                                        ? announcements.map((a, i) => (
                                            <div key={i} className="news-item" title={a.body}>{a.title}</div>
                                          ))
                                        : <>
                                            <div className="news-item">Bicoin officially launched with advanced security.</div>
                                            <div className="news-item">New pairs listed: SOL/USDT, AVAX/USDT.</div>
                                          </>
                                }
                            </div>
                        </div>
                    </div>
                </div>

                {/* Hero Section */}
                <section className="hero-section">
                    <div className="hero-content">
                        <div className="hero-text">
                            <h1 className="hero-title">
                                Start Your Crypto<br />
                                Journey With<br />
                                <span className="hero-highlight-gradient">Confidence</span>
                            </h1>
                            <p className="hero-description">
                                Experience the next generation of digital asset trading.
                                Deep liquidity, advanced security, and seamless performance
                                for professional traders.
                            </p>
                            <div className="hero-cta-group">
                                <button className="hero-cta" onClick={() => navigate('/signup')}>Get started</button>
                            </div>
                        </div>
                        <div className="hero-right">
                            <img src="/11.webp" alt="Trading Visual" className="hero-main-img" />
                        </div>
                    </div>
                </section>

                {/* Market Section - Moved to 2nd */}
                <section className="market-section-landing">
                    <div className="market-header-landing">
                        <h2 className="section-title">Explore Live Market Opportunities</h2>
                        <p className="section-subtitle">View real-time prices, market movements, and trading volume across popular crypto pairs, and access spot trading directly from the market list.</p>
                    </div>
                    <div className="market-table-container">
                        <table className="market-table-landing">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Market</th>
                                    <th>Last Price</th>
                                    <th>Change</th>
                                    <th>24H Volume</th>
                                    <th>Trade</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mobileCryptoData.map((coin, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <div className="market-coin-info">
                                                <img src={coin.logo} alt={coin.symbol} className="market-coin-logo" />
                                                <span className="market-coin-symbol">{coin.symbol}</span>
                                                <span className="market-coin-pair">{coin.name}</span>
                                            </div>
                                        </td>
                                        <td>{coin.price}</td>
                                        <td className={coin.positive ? 'positive' : 'negative'}>{coin.change}</td>
                                        <td>{coin.volume}</td>
                                        <td>
                                            <button className="trade-btn" onClick={() => navigate(`/dashboard/trade?pair=${coin.symbol}/USDT`)}>Trade</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="view-more-market" onClick={() => navigate('/dashboard/market')}>
                            View More Assets <TrendingUp size={16} />
                        </div>
                    </div>
                </section>

                {/* More Ways Section - 3rd */}
                <section className="more-ways-section-v2">
                    <div className="section-header-centered">
                        <h2 className="section-title">More Ways To Access Crypto Markets</h2>
                        <p className="section-subtitle">
                            Explore additional options to buy, manage, and move your assets within your <br />
                            Bicoin account using secure and user-friendly tools.
                        </p>
                    </div>
                    <div className="ways-grid-v2">
                        <div className="way-card-v2">
                            <div className="way-visual-v2">
                                <img src="/7.webp" alt="Purchases" />
                            </div>
                            <h3 className="way-title-v2">Simple Crypto Purchases</h3>
                            <p className="way-desc-v2">Buy crypto easily using supported payment methods, with a straightforward process</p>
                        </div>
                        <div className="way-card-v2">
                            <div className="way-visual-v2">
                                <img src="/8.webp" alt="Partners" />
                            </div>
                            <h3 className="way-title-v2">Trusted Payment Partners</h3>
                            <p className="way-desc-v2">Complete crypto purchases through verified third-party providers, ensuring secure transactions</p>
                        </div>
                        <div className="way-card-v2">
                            <div className="way-visual-v2">
                                <img src="/9.webp" alt="Wallet" />
                            </div>
                            <h3 className="way-title-v2">Direct Wallet Credit</h3>
                            <p className="way-desc-v2">Purchased crypto is credited directly to your Bicoin wallet</p>
                        </div>
                    </div>
                </section>

                {/* Features Section - Built for Reliable Trading */}
                <section className="features-section">
                    <div className="section-header-centered">
                        <h2 className="section-title">Built For A Reliable Trading Experience</h2>
                        <p className="section-subtitle">
                            Access essential tools, secure infrastructure, and platform features designed to <br />
                            support efficient trading and asset management on Bicoin.
                        </p>
                    </div>
                    <div className="features-grid-v2">
                        <div className="feature-card-v2">
                            <div className="feature-icon-v2"><ShieldCheck size={24} /></div>
                            <h3 className="feature-title-v2">Maximum Security</h3>
                            <p className="feature-desc-v2">Your assets are protected with cutting-edge security protocols.</p>
                        </div>
                        <div className="feature-card-v2">
                            <div className="feature-icon-v2"><TrendingUp size={24} /></div>
                            <h3 className="feature-title-v2">Instant Transactions</h3>
                            <p className="feature-desc-v2">Execute your transactions in real-time, without delays.</p>
                        </div>
                        <div className="feature-card-v2">
                            <div className="feature-icon-v2"><TrendingUp size={24} /></div>
                            <h3 className="feature-title-v2">Optimized Fees</h3>
                            <p className="feature-desc-v2">Benefit from some of the lowest fees on the market.</p>
                        </div>
                        <div className="feature-card-v2">
                            <div className="feature-icon-v2"><Repeat size={24} /></div>
                            <h3 className="feature-title-v2">Seamless Trading Tools</h3>
                            <p className="feature-desc-v2">Use integrated trading tools built to support spot and futures</p>
                        </div>
                        <div className="feature-card-v2">
                            <div className="feature-icon-v2"><TrendingUp size={24} /></div>
                            <h3 className="feature-title-v2">Affiliate Program</h3>
                            <p className="feature-desc-v2">Benefit from some of the lowest fees on the market.</p>
                        </div>
                    </div>
                </section>


                {/* Confidence Section - 5th */}
                <section className="confidence-section">
                    <div className="confidence-content">
                        <div className="confidence-text">
                            <h1 className="confidence-title">Trade And Manage Crypto With Confidence</h1>
                            <p className="confidence-subtitle">
                                Use a secure and reliable platform to access crypto markets,
                                manage balances, and perform transactions across devices.
                            </p>
                        </div>
                        <div className="confidence-visual">
                            <img src="/10.webp" alt="Trade Confidence" className="confidence-main-img" />
                        </div>
                    </div>
                </section>

                {/* CTA Banner Section */}
                <section className="cta-banner-section">
                    <div className="cta-banner-card">
                        <div className="cta-banner-content">
                            <h2 className="cta-title">Start Trading On Bicoin</h2>
                            <p className="cta-subtitle">
                                Create an account to access live markets, manage your assets, and <br />
                                trade across supported products within a secure platform environment.
                            </p>
                            <button className="cta-main-btn" onClick={() => navigate('/dashboard/deposit')}>Deposit crypto</button>
                        </div>
                    </div>
                </section>
            </div>
        </Layout>
    )
}



